import { zodResolver } from '@hookform/resolvers/zod';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import Button from 'components/actions/Button';
import type { TeamSummaryFE } from 'domain/systemAdmin';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useListSystemFleetsQuery } from 'services/systemAdmin/fleets';
import { useListUsersQuery } from 'services/user';
import { getTeamFormData, setTeamFormData, toogleShowRoleDialog } from 'store/slices/settingsPageSlice';

import FormFieldInput from './FormFieldInput';
import FormFieldMultiSelect from './FormFieldMultiSelect';
import { createTeamFormSchema, editTeamFormSchema } from './schemas';
import type { TeamFormData } from './types';
import UsersTeamTable from './UsersTeamTable';

import './_styles.scss';

import classnames from 'classnames';

interface TeamFormProps {
    onSubmit: (data: TeamFormData) => void;
    onCancel: () => void;
    isLoading: boolean;
    isExecuting: boolean;
    team: TeamSummaryFE | undefined;
}

const TeamForm = ({ team, onSubmit, onCancel, isLoading, isExecuting }: TeamFormProps) => {
    const dispatch = useDispatch();
    const teamFormData = useSelector(getTeamFormData);
    const {
        data: userData,
        isLoading: usersLoading,
        isFetching: isFetchingUsers,
    } = useListUsersQuery('ListUsers', {
        refetchOnMountOrArgChange: true,
    });

    const { data: fleetData, isLoading: fleetsLoading } = useListSystemFleetsQuery('ListSystemFleets', {
        refetchOnMountOrArgChange: true,
    });

    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

    const schema = team ? editTeamFormSchema() : createTeamFormSchema();

    const defaultValues = useMemo<TeamFormData>(
        () => ({
            id: team?.id ?? teamFormData?.id ?? '',
            name: team?.name ?? teamFormData?.name ?? '',
            fleets: team?.id
                ? (fleetData?.filter(f => f.teams.some(t => t.id === team.id)).map(f => f.id) ?? [])
                : (teamFormData?.fleets ?? []),
            users: team?.id
                ? (userData?.filter(u => u.teams.some(t => t.id === team.id)).map(u => u.id) ?? [])
                : (teamFormData?.users ?? []),
        }),
        [team, teamFormData, fleetData, userData]
    );

    const { handleSubmit, formState, control, setValue, watch } = useForm<TeamFormData>({
        resolver: zodResolver(schema),
        defaultValues,
        mode: 'all',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        if (!userData || !fleetData) {
            return;
        }
        if (!teamFormData || Object.keys(teamFormData).length === 0) {
            dispatch(setTeamFormData(defaultValues));
            Object.entries(defaultValues).forEach(([key, value]) => setValue(key as keyof TeamFormData, value));
        }
    }, [defaultValues, userData, fleetData, dispatch, setValue, teamFormData]);

    useEffect(() => {
        const subscription = watch(values => {
            dispatch(setTeamFormData(values as TeamFormData));
        });
        return () => subscription.unsubscribe();
    }, [watch, dispatch]);

    useEffect(() => {
        if (!team || !userData) {
            return;
        }
        const ids = userData.filter(u => u.teams.some(t => t.id === team.id)).map(u => u.id);
        setSelectedUserIds(ids);
    }, [team, userData]);

    useEffect(() => {
        setValue('users', selectedUserIds, { shouldValidate: true, shouldDirty: true });
    }, [selectedUserIds, setValue]);

    const toggleUserSelection = (id: string) => {
        setSelectedUserIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };

    const toogleRoleDialog = () => {
        dispatch(toogleShowRoleDialog());
    };

    if (usersLoading || fleetsLoading) {
        return (
            <div className='TeamForm__spinner'>
                <Spinner text={'Loading...'} show isFullSized />
            </div>
        );
    }

    if (!userData || !fleetData) {
        return null;
    }

    const fleetOptions: SelectOption[] = fleetData.map(f => ({
        id: f.id,
        label: f.name,
    }));

    const onFormSubmit = (data: TeamFormData) => {
        onSubmit({
            ...data,
            users: selectedUserIds,
        });
    };

    return (
        <form className='TeamForm__form' onSubmit={handleSubmit(onFormSubmit)}>
            <p className='TeamForm__bodyText'>Operational</p>
            <div className='TeamForm__bodyRow'>
                <div className='TeamForm__bodyColumn'>
                    <FormFieldInput
                        label='Name'
                        placeholder='Insert name'
                        required
                        name='name'
                        control={control}
                        error={formState.errors.name}
                    />
                </div>
                <div className='TeamForm__bodyColumn'>
                    <FormFieldMultiSelect
                        control={control}
                        label='Fleet(s)'
                        placeholder='Select fleet(s)'
                        name='fleets'
                        error={
                            Array.isArray(formState.errors?.fleets)
                                ? formState.errors?.fleets[0]
                                : formState.errors?.fleets
                        }
                        options={fleetOptions}
                    />
                </div>
            </div>
            <br />
            <p className='TeamForm__bodyText'>Users</p>
            <div className='TeamForm__bodyRow'>
                <UsersTeamTable
                    users={userData}
                    openDialogInfo={toogleRoleDialog}
                    selectedUserIds={selectedUserIds}
                    onToggleUser={toggleUserSelection}
                    isLoading={isLoading || isFetchingUsers}
                />
            </div>
            <hr className='TeamForm__divider' />
            <div className='TeamForm__buttons'>
                <Button disabled={isLoading || isExecuting} data-testid='user-dialog-cancel' onClick={onCancel}>
                    Cancel
                </Button>
                <OverlayTrigger
                    placement='top'
                    overlay={
                        <Tooltip
                            className={classnames('ButtonWithTooltip__tooltip', {
                                'ButtonWithTooltip__tooltip--hidden': !(!formState.isValid || isLoading),
                            })}
                        >
                            <p>Complete the mandatory missing fields to continue</p>
                        </Tooltip>
                    }
                >
                    <span>
                        <Button
                            disabled={isLoading || !formState.isValid || isExecuting}
                            type='submit'
                            data-testid='create-team-dialog-confirm'
                            style='primary'
                        >
                            {isLoading || isExecuting ? (
                                <>
                                    <span className='rioglyph rioglyph-spinner animate-spin' />
                                    {team !== undefined && team.id !== undefined
                                        ? 'Saving changes'
                                        : 'Creating new team'}
                                </>
                            ) : (
                                <>
                                    {team !== undefined && team.id !== undefined ? (
                                        <></>
                                    ) : (
                                        <span className='rioglyph rioglyph-plus' />
                                    )}
                                    {team !== undefined && team.id !== undefined ? 'Save changes' : 'Create new team'}
                                </>
                            )}
                        </Button>
                    </span>
                </OverlayTrigger>
            </div>
        </form>
    );
};

export default TeamForm;
